import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageMeta from '@/components/feature/PageMeta';
import { externalLinks, siteSettings } from '@/mocks/siteSettings';
import { useScrollReveal } from '@/hooks/useScrollReveal';

function RevealWrap({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const { ref, isVisible } = useScrollReveal(0.06);
  return (
    <div ref={ref} className={`section-reveal ${isVisible ? 'is-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

function OrnamentDivider() {
  return (
    <div className="ornament-divider my-12 md:my-16" aria-hidden="true">
      <div className="ornament-divider-icon font-display italic">&#10087;</div>
    </div>
  );
}

const publishingCategories = [
  '中國歷史', '世界歷史', '近代史與民國史', '歷史人物',
  '古典文學', '傳統文化', '思想經典', '古籍整理', '經典文本重新編排',
];

export default function About() {
  return (
    <>
      <PageMeta
        title="關於複刻文化 Fuko｜歷史、人文社科、公版經典與小說"
        description="了解複刻文化的品牌定位與出版方向。複刻文化以歷史、人文社科、公版經典與小說為主要出版方向，本品牌委託崧博出版經銷，官方網站由崧博出版製作。"
        canonical="https://fuko.sonbook.net/about"
        ogTitle="關於複刻文化｜歷史、人文社科、公版經典與小說"
        ogDescription="認識複刻文化的品牌定位，了解品牌如何以歷史、人文社科、公版經典與小說為出版方向，讓不同時代的作品重新進入當代閱讀。"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            '@id': 'https://fuko.sonbook.net/about#webpage',
            url: 'https://fuko.sonbook.net/about',
            name: '關於複刻文化',
            description: '了解複刻文化的品牌定位與出版方向。複刻文化以歷史、人文社科、公版經典與小說為主要出版方向，本品牌委託崧博出版經銷。',
            inLanguage: 'zh-TW',
            isPartOf: { '@id': 'https://fuko.sonbook.net/#website' },
            about: { '@id': 'https://fuko.sonbook.net/#organization' },
            breadcrumb: { '@id': 'https://fuko.sonbook.net/about#breadcrumb' },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            '@id': 'https://fuko.sonbook.net/about#breadcrumb',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首頁',
                item: 'https://fuko.sonbook.net/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '品牌介紹',
                item: 'https://fuko.sonbook.net/about',
              },
            ],
          },
        ]}
      />
      <Navbar />
      <main>

        {/* §1 · HERO */}
        <section className="relative overflow-hidden paper-texture min-h-screen flex items-center" style={{ backgroundColor: 'oklch(var(--background-100))' }}>
          <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
            <img src="https://readdy.ai/api/search-image?query=An%20antique%20wooden%20writing%20desk%20with%20scattered%20manuscript%20pages%20in%20both%20Chinese%20vertical%20columns%20of%20elegant%20calligraphy%20and%20Western%20cursive%20script%20written%20with%20a%20vintage%20fountain%20pen%2C%20a%20single%20vintage%20hardcover%20book%20in%20dark%20burgundy%20cloth%20binding%20lying%20partially%20open%20revealing%20cream-colored%20aged%20pages%20with%20deckle%20edges%2C%20soft%20warm%20light%20from%20a%20brass%20desk%20lamp%20creating%20a%20golden%20glow%20across%20the%20desk%20surface%20and%20casting%20gentle%20shadows%2C%20the%20background%20is%20a%20softly%20blurred%20bookshelf%20with%20rows%20of%20old%20leather-bound%20volumes%20in%20muted%20earth%20tones%20alongside%20Chinese%20thread-bound%20books%20with%20ivory%20title%20slips%2C%20a%20small%20Qing%20dynasty-style%20porcelain%20brush%20rest%20beside%20a%20European%20inkwell%20with%20a%20quill%2C%20modern%20minimalist%20editorial%20publishing%20studio%20aesthetic%20with%20scholarly%20archival%20atmosphere%20blending%20Eastern%20and%20Western%20classical%20traditions%2C%20intellectual%20contemplative%20mood%2C%20refined%20and%20restrained%20without%20being%20dramatic%20or%20ornate%2C%20fine%20art%20documentary%20photography%20style%2C%20deep%20warm%20neutral%20color%20palette%20of%20amber%20golden%20brown%20cream%20and%20deep%20burgundy%2C%20sophisticated%20cultural%20publishing%20visual%20identity%2C%20clean%20composition%20with%20balanced%20negative%20space%2C%20editorial%20magazine%20quality%20photograph&width=1600&height=900&seq=fuko-about-hero-vintage-01&orientation=landscape&nocache=true" alt="" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: 0.32 }} />
            <span className="absolute top-8 right-[10%] font-display italic text-[11px] hidden md:block" style={{ color: 'oklch(var(--foreground-400)/0.22)' }}>P. 011</span>
            <span className="absolute top-[6%] left-[11%] font-label text-[9px] tracking-[0.4em] hidden md:block" style={{ color: 'oklch(var(--foreground-400)/0.14)' }}>FUKO ARCHIVE</span>
            <span className="absolute font-heading font-black leading-none select-none hidden xl:block"
              style={{ fontSize: 260, color: 'oklch(var(--foreground-950)/0.016)', bottom: 20, right: '6%', lineHeight: 0.75 }}
            >複</span>
          </div>

          <div className="relative w-full px-6 md:px-12 lg:px-16 pt-20 md:pt-24 pb-20 md:pb-28" style={{ zIndex: 1 }}>
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-0">
              <div className="hidden lg:flex lg:w-[16%] flex-col items-start pt-2">
                <div className="flex items-center gap-3 mb-4" style={{ color: 'oklch(var(--foreground-400)/0.42)' }}>
                  <span className="font-display italic font-bold text-[13px]">I</span>
                  <span className="w-6 h-px" style={{ backgroundColor: 'oklch(var(--foreground-300)/0.20)' }} />
                  <span className="font-label text-[11px] tracking-[0.2em] uppercase">About</span>
                </div>
                <span className="font-heading font-bold leading-none select-none" style={{ fontSize: 'clamp(72px,8vw,100px)', color: 'oklch(var(--foreground-950)/0.018)', lineHeight: 0.75 }}
                >複</span>
                <span className="font-heading font-bold leading-none select-none" style={{ fontSize: 'clamp(72px,8vw,100px)', color: 'oklch(var(--foreground-950)/0.014)', lineHeight: 0.75 }}
                >刻</span>
              </div>

              <div className="lg:w-[68%] lg:px-10 lg:border-l" style={{ borderColor: 'oklch(var(--foreground-950)/0.06)' }}>
                <div className="flex items-center gap-3 mb-4 lg:hidden" style={{ color: 'oklch(var(--foreground-400)/0.42)' }}>
                  <span className="font-display italic font-bold text-[13px]">I</span>
                  <span className="w-6 h-px" style={{ backgroundColor: 'oklch(var(--foreground-300)/0.20)' }} />
                  <span className="font-label text-[11px] tracking-[0.2em] uppercase">About</span>
                </div>
                <h1 className="font-heading font-bold text-foreground-900 leading-[1.15] mb-2" style={{ fontSize: 'clamp(2rem,4vw,3.5rem)', letterSpacing: '-0.01em' }}>關於複刻文化</h1>
                <p className="font-display italic font-bold mb-6" style={{ fontSize: 'clamp(1rem,1.5vw,1.3rem)', color: 'oklch(var(--primary-500))' }}>
                  Fuko
                </p>

                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  複刻文化以歷史、人文社科、公版經典與小說為主要出版方向，透過整理、編輯與出版，讓不同時代的作品以適合當代讀者的形式重新進入閱讀視野。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  複刻文化所強調的「複刻」，不是單純複製舊內容，而是整理文本脈絡、理解作品背景，並建立連結過去與當代的閱讀入口。
                </p>
                <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                  本品牌委託崧博出版經銷，官方網站由崧博出版製作。本頁提供品牌定位、出版方向與正式書目查詢資訊。
                </p>
              </div>

              <div className="hidden lg:block lg:w-[16%]"></div>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 h-px" style={{ backgroundColor: 'oklch(var(--foreground-950)/0.07)' }} />
        </section>

        <OrnamentDivider />

        {/* §2 · 認識複刻文化 + 品牌定位 */}
        <RevealWrap>
          <section className="relative overflow-hidden py-20 md:py-28" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
            <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
              <span className="absolute right-[10%] top-[8%] font-display italic text-[11px] hidden md:block" style={{ color: 'oklch(var(--foreground-400)/0.12)' }}>P. 012</span>
              <span className="absolute bottom-8 left-[8%] font-display italic font-bold select-none hidden lg:block" style={{ fontSize: 96, color: 'oklch(var(--foreground-950)/0.014)' }}>Fuko</span>
              <span className="absolute top-[8%] right-[5%] font-heading font-bold select-none hidden xl:block" style={{ fontSize: 72, color: 'oklch(var(--foreground-950)/0.011)' }}>複刻</span>
            </div>

            <div className="relative w-full px-6 md:px-12 lg:px-16">
              <div className="max-w-[min(100%,1000px)] mx-auto">
                <div className="mb-20">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">認識複刻文化</h2>
                  <div className="space-y-4">
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                      複刻文化是以歷史、人文社科、公版經典與小說為主要出版方向的出版品牌，出版內容涵蓋歷史、人文社科、公版經典、小說及其他具有文化與思想價值的作品。
                    </p>
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                      品牌透過文本整理、閱讀脈絡補充與當代視角，協助讀者理解作品所處的歷史背景、人物選擇、思想發展與社會環境。
                    </p>
                    <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                      複刻文化希望讓經典不只停留在學術研究、歷史記憶或收藏書架上，而能以更清楚、易於理解的方式重新進入當代生活。
                    </p>
                  </div>
                </div>

                <div className="classical-rule mb-16" />

                <div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">複刻文化的品牌定位</h2>
                  <div className="space-y-4">
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                      複刻文化以歷史、人文社科、公版經典與小說為主要出版方向，透過重新整理文本、補充閱讀脈絡及連結當代議題，建立讀者重新進入經典作品的入口。
                    </p>
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                      品牌重視作品的歷史背景、思想脈絡與文化價值，但不應將所有出版品描述為具有相同的翻譯、校訂、註釋或編輯方式，實際內容仍以個別書籍資料為準。
                    </p>
                    <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
                      複刻文化所指的「複刻」是閱讀與編輯層面的重新整理與重新理解，不是文物仿製、古籍修復或收藏品販售。
                    </p>
                  </div>

                  <div className="mt-12 pt-6 vintage-corner p-6">
                    <p className="font-display italic text-[11px] tracking-[0.30em] mb-4" style={{ color: 'oklch(var(--foreground-400)/0.38)' }}>BRAND INDEX</p>
                    <div className="space-y-2.5">
                      {[
                        { label: '委託崧博出版經銷', accent: 'oklch(var(--primary-400))' },
                        { label: '歷史 · 人文社科 · 公版經典 · 小說', accent: 'oklch(var(--secondary-400))' },
                        { label: '正式書目查詢入口', accent: 'oklch(var(--accent-400))' },
                      ].map((item) => (
                        <div key={item.label} className="flex items-center gap-3">
                          <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.accent }} />
                          <span className="text-xs text-foreground-500 font-label">{item.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </RevealWrap>

        <OrnamentDivider />

        {/* §3 · 複刻文化的出版內容 */}
        <RevealWrap>
          <section className="relative overflow-hidden py-20 md:py-28 paper-texture" style={{ backgroundColor: 'oklch(var(--background-100))' }}>
            <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
              <span className="absolute left-[17%] top-[5%] font-display italic text-[10px] hidden lg:block" style={{ color: 'oklch(var(--foreground-400)/0.12)' }}>&#182;</span>
              <span className="absolute right-[10%] top-[5%] font-display italic text-[11px] hidden md:block" style={{ color: 'oklch(var(--foreground-400)/0.12)' }}>P. 013</span>
              <span className="absolute bottom-8 right-[6%] font-heading font-bold select-none hidden lg:block" style={{ fontSize: 100, color: 'oklch(var(--foreground-950)/0.013)' }}>目錄</span>
            </div>

            <div className="relative w-full px-6 md:px-12 lg:px-16" style={{ zIndex: 1 }}>
              <div className="max-w-[min(100%,1000px)] mx-auto">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">複刻文化的出版內容</h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-12">
                  複刻文化以歷史、人文社科、公版經典與小說為主要出版方向，實際作品分類與內容仍以網站現有正式書籍及書目資料為準。
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 mb-12">
                  <div className="vintage-corner p-5">
                    <h3 className="font-heading font-bold text-foreground-900 text-lg mb-3">歷史</h3>
                    <p className="text-sm text-foreground-600 leading-[1.8]">
                      透過歷史事件、人物、制度與社會變化，協助讀者理解不同時代的形成背景與發展脈絡。
                    </p>
                  </div>
                  <div className="vintage-corner p-5">
                    <h3 className="font-heading font-bold text-foreground-900 text-lg mb-3">人文社科</h3>
                    <p className="text-sm text-foreground-600 leading-[1.8]">
                      出版與人文思想、社會議題及文化觀察相關的閱讀內容。
                    </p>
                  </div>
                  <div className="vintage-corner p-5">
                    <h3 className="font-heading font-bold text-foreground-900 text-lg mb-3">公版經典</h3>
                    <p className="text-sm text-foreground-600 leading-[1.8]">
                      讓不同時代的經典作品重新進入當代閱讀。
                    </p>
                  </div>
                  <div className="vintage-corner p-5">
                    <h3 className="font-heading font-bold text-foreground-900 text-lg mb-3">小說</h3>
                    <p className="text-sm text-foreground-600 leading-[1.8]">
                      出版不同題材與形式的小說作品。
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {publishingCategories.map((item) => (
                    <span key={item}
                      className="inline-block text-xs font-label px-3.5 py-2 cursor-default transition-colors duration-200"
                      style={{
                        backgroundColor: 'oklch(var(--background-50))',
                        border: '1px solid oklch(var(--foreground-200)/0.6)',
                        color: 'oklch(var(--foreground-600))',
                        borderRadius: 2,
                      }}
                      onMouseEnter={e => {
                        const el = e.currentTarget as HTMLElement;
                        el.style.backgroundColor = 'oklch(var(--primary-50))';
                        el.style.borderColor = 'oklch(var(--primary-300)/0.6)';
                        el.style.color = 'oklch(var(--primary-700))';
                      }}
                      onMouseLeave={e => {
                        const el = e.currentTarget as HTMLElement;
                        el.style.backgroundColor = 'oklch(var(--background-50))';
                        el.style.borderColor = 'oklch(var(--foreground-200)/0.6)';
                        el.style.color = 'oklch(var(--foreground-600))';
                      }}
                    >{item}</span>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </RevealWrap>

        <OrnamentDivider />

        {/* §4 · 如何重新呈現經典 + 閱讀特色 */}
        <RevealWrap>
          <section className="relative overflow-hidden py-20 md:py-28" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
            <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
              <span className="absolute right-[8%] top-[6%] font-display italic text-[11px] hidden md:block" style={{ color: 'oklch(var(--foreground-400)/0.12)' }}>P. 014</span>
              <span className="absolute bottom-10 left-[7%] font-heading font-bold select-none hidden lg:block" style={{ fontSize: 90, color: 'oklch(var(--foreground-950)/0.012)' }}>方法</span>
            </div>

            <div className="relative w-full px-6 md:px-12 lg:px-16">
              <div className="max-w-[min(100%,1000px)] mx-auto">
                <div className="mb-20">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">複刻文化如何重新呈現經典？</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="vintage-corner p-5">
                      <h3 className="font-heading font-bold text-foreground-900 text-base mb-3">整理文本</h3>
                      <p className="text-sm text-foreground-600 leading-[1.8]">
                        整理篇章結構、內容順序與閱讀脈絡，協助讀者理解作品的基本結構。
                      </p>
                    </div>
                    <div className="vintage-corner p-5">
                      <h3 className="font-heading font-bold text-foreground-900 text-base mb-3">補充背景</h3>
                      <p className="text-sm text-foreground-600 leading-[1.8]">
                        從歷史環境、人物經驗與思想發展理解作品形成的時代條件。
                      </p>
                    </div>
                    <div className="vintage-corner p-5">
                      <h3 className="font-heading font-bold text-foreground-900 text-base mb-3">連結當代</h3>
                      <p className="text-sm text-foreground-600 leading-[1.8]">
                        透過當代閱讀視角，重新思考經典作品與今天的社會、制度及生活問題。
                      </p>
                    </div>
                  </div>
                </div>

                <div className="classical-rule mb-16" />

                <div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">複刻文化的閱讀特色</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div>
                      <h3 className="font-heading font-bold text-foreground-900 text-base mb-3">重新整理</h3>
                      <p className="text-sm text-foreground-600 leading-[1.8]">
                        整理文本內容、篇章結構與閱讀脈絡，協助讀者進入跨越不同年代的作品。
                      </p>
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-foreground-900 text-base mb-3">重新理解</h3>
                      <p className="text-sm text-foreground-600 leading-[1.8]">
                        透過歷史背景、思想脈絡與人物選擇，理解作品所反映的時代與社會問題。
                      </p>
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-foreground-900 text-base mb-3">重新閱讀</h3>
                      <p className="text-sm text-foreground-600 leading-[1.8]">
                        讓曾經熟悉或被忽略的經典，在新的閱讀情境中產生新的理解與價值。
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </RevealWrap>

        <OrnamentDivider />

        {/* §5 · 適合哪些讀者 + 為什麼重新閱讀經典 */}
        <RevealWrap>
          <section className="relative overflow-hidden py-20 md:py-28 paper-texture" style={{ backgroundColor: 'oklch(var(--background-100))' }}>
            <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
              <span className="absolute right-[8%] top-[6%] font-display italic text-[11px] hidden md:block" style={{ color: 'oklch(var(--foreground-400)/0.12)' }}>P. 015</span>
              <span className="absolute top-[6%] right-[5%] font-heading font-bold select-none hidden xl:block" style={{ fontSize: 72, color: 'oklch(var(--foreground-950)/0.011)' }}>讀者</span>
            </div>

            <div className="relative w-full px-6 md:px-12 lg:px-16" style={{ zIndex: 1 }}>
              <div className="max-w-[min(100%,1000px)] mx-auto">
                <div className="mb-20">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">複刻文化適合哪些讀者？</h2>
                  <div className="space-y-4">
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                      適合對歷史、人文社科、公版經典、小說、人物故事、思想文化與社會變遷感興趣，並希望重新理解經典作品的讀者。
                    </p>
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                      讀者可依網站目前公開的書籍主題，查找符合個人興趣與閱讀需求的歷史、人文社科及經典作品。
                    </p>
                  </div>
                </div>

                <div className="classical-rule mb-16" />

                <div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">為什麼需要重新閱讀經典？</h2>
                  <div className="space-y-4">
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                      經典作品形成於特定的歷史、文化與社會背景。重新閱讀不只是回顧過去，也能協助讀者理解思想如何形成，以及不同時代的人如何面對權力、制度、選擇與生活問題。
                    </p>
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                      透過理解作品背景與閱讀脈絡，讀者能更清楚辨識經典內容與當代生活之間的差異、連結及可重新思考之處。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </RevealWrap>

        <OrnamentDivider />

        {/* §6 · 品牌關係 + 書目導流 + 通路導流 */}
        <RevealWrap>
          <section className="relative overflow-hidden py-20 md:py-28" style={{ backgroundColor: 'oklch(var(--background-50))' }}>
            <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
              <span className="absolute right-[8%] top-[5%] font-display italic text-[11px] hidden md:block" style={{ color: 'oklch(var(--foreground-400)/0.12)' }}>P. 016</span>
              <span className="absolute bottom-8 left-[6%] font-heading font-bold select-none hidden lg:block" style={{ fontSize: 90, color: 'oklch(var(--foreground-950)/0.012)' }}>索引</span>
            </div>

            <div className="relative w-full px-6 md:px-12 lg:px-16">
              <div className="max-w-[min(100%,1000px)] mx-auto">
                <div className="mb-16">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">複刻文化與崧博出版</h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
                    本品牌委託崧博出版經銷，官方網站由崧博出版製作。讀者可透過複刻文化品牌網站瀏覽相關出版內容，並前往崧博書籍資料庫查詢完整書目資訊。
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <a href={externalLinks.songbo} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-medium whitespace-nowrap transition-colors"
                      style={{ padding: '10px 20px', backgroundColor: 'oklch(var(--secondary-600))', color: 'oklch(var(--background-50))', borderRadius: 2 }}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'oklch(var(--secondary-700))')}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'oklch(var(--secondary-600))')}
                    >
                      前往崧博出版<i className="ri-external-link-line text-xs"></i>
                    </a>
                    <a href={externalLinks.catalog} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-medium whitespace-nowrap transition-colors"
                      style={{ padding: '10px 20px', backgroundColor: 'oklch(var(--background-50))', border: '1px solid oklch(var(--foreground-300)/0.35)', color: 'oklch(var(--foreground-700))', borderRadius: 2 }}
                      onMouseEnter={e => {
                        const el = e.currentTarget as HTMLElement;
                        el.style.backgroundColor = 'oklch(var(--background-100))';
                        el.style.borderColor = 'oklch(var(--foreground-300)/0.5)';
                      }}
                      onMouseLeave={e => {
                        const el = e.currentTarget as HTMLElement;
                        el.style.backgroundColor = 'oklch(var(--background-50))';
                        el.style.borderColor = 'oklch(var(--foreground-300)/0.35)';
                      }}
                    >
                      前往書籍資料庫<i className="ri-external-link-line text-xs"></i>
                    </a>
                  </div>
                </div>

                <div className="classical-rule mb-16" />

                <div className="mb-16">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">查詢複刻文化正式書目</h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                    如需確認複刻文化出版作品的完整書名、作者、ISBN、出版日期及其他正式出版資訊，請前往崧博出版書籍資料庫查詢。
                  </p>
                  <a href={externalLinks.catalog} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 font-medium rounded-sm text-sm whitespace-nowrap transition-colors"
                    style={{ padding: '12px 26px', backgroundColor: 'oklch(var(--primary-500))', color: 'oklch(var(--background-50))' }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'oklch(var(--primary-600))')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'oklch(var(--primary-500))')}
                  >
                    查詢完整書目<i className="ri-external-link-line text-xs"></i>
                  </a>
                </div>

                <div className="classical-rule mb-16" />

                <div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">查看複刻文化合作購書通路</h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                    複刻文化相關書籍可透過網站已建立的合作購書通路查找。各作品在不同平台的上架狀態、版本、價格及庫存可能不同，實際資訊請以各通路頁面為準。
                  </p>
                  <Link to="/stores"
                    className="inline-flex items-center gap-2 font-medium rounded-sm text-sm whitespace-nowrap transition-colors"
                    style={{ padding: '12px 26px', border: '1px solid oklch(var(--foreground-300)/0.35)', color: 'oklch(var(--foreground-700))' }}
                    onMouseEnter={e => {
                      const el = e.currentTarget as HTMLElement;
                      el.style.backgroundColor = 'oklch(var(--background-100))';
                      el.style.borderColor = 'oklch(var(--foreground-300)/0.5)';
                    }}
                    onMouseLeave={e => {
                      const el = e.currentTarget as HTMLElement;
                      el.style.backgroundColor = 'transparent';
                      el.style.borderColor = 'oklch(var(--foreground-300)/0.35)';
                    }}
                  >
                    查看合作購書通路<i className="ri-arrow-right-line text-xs"></i>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </RevealWrap>

        <OrnamentDivider />

        {/* §7 · 品牌經銷與合作洽詢 */}
        <RevealWrap>
          <section className="relative overflow-hidden py-20 md:py-28 paper-texture" style={{ backgroundColor: 'oklch(var(--background-100))' }}>
            <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
              <span className="absolute right-[8%] top-[6%] font-display italic text-[11px] hidden md:block" style={{ color: 'oklch(var(--foreground-400)/0.12)' }}>P. 017</span>
              <span className="absolute bottom-8 right-[6%] font-heading font-bold select-none hidden lg:block" style={{ fontSize: 90, color: 'oklch(var(--foreground-950)/0.012)' }}>合作</span>
            </div>

            <div className="relative w-full px-6 md:px-12 lg:px-16" style={{ zIndex: 1 }}>
              <div className="max-w-[min(100%,1000px)] mx-auto">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">品牌經銷與合作洽詢</h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-3">
                  如有複刻文化品牌經銷、通路合作、內容合作、媒體或其他相關合作需求，請透過網站現有的官方聯絡方式提出詢問。
                </p>
                <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-8">
                  品牌經銷與合作方案詳情請直接聯繫洽詢。
                </p>
                <div className="flex flex-wrap gap-3">
                  <a href={externalLinks.songbo} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium whitespace-nowrap transition-colors"
                    style={{ padding: '10px 20px', backgroundColor: 'oklch(var(--secondary-600))', color: 'oklch(var(--background-50))', borderRadius: 2 }}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'oklch(var(--secondary-700))')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'oklch(var(--secondary-600))')}
                  >
                    前往崧博出版<i className="ri-external-link-line text-xs"></i>
                  </a>
                  <a href={`mailto:${siteSettings.email}`}
                    className="inline-flex items-center gap-1.5 text-sm font-medium whitespace-nowrap transition-colors btn-underline"
                    style={{ padding: '10px 20px', color: 'oklch(var(--primary-600))' }}
                  >
                    聯絡合作洽詢<i className="ri-arrow-right-line text-xs"></i>
                  </a>
                </div>
              </div>
            </div>
          </section>
        </RevealWrap>

      </main>
      <Footer />
    </>
  );
}